import { createFileRoute } from "@tanstack/react-router"
import { Check, X, Zap, AlertCircle, ArrowRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { LifetimeAccessPricing } from "@/components/life-time-access-pricing"
import { createLLMOMeta, createBreadcrumbSchema, createFAQSchema, createArticleSchema } from "@/lib/seo"
import { useAuth } from "@/components/auth/AuthProvider"
import { useAnalytics } from "@/lib/analytics"
import { Paragraph, ContentSection } from "@/components/tools/SeoContent"
import { useState } from "react"
import { AskAIExplain } from "@/components/ask-ai-to-explain"

// ─── FAQ Data ────────────────────────────────────────────────
const faqs = [
  {
    question: "Is Zapify better than LinkDM?",
    answer:
      "For Instagram-first creators, yes. Zapify offers unlimited DMs on paid plans, AI-powered replies, brand voice controls, ask-to-follow flows, email capture, follower and lead attribution, templates, smart offers, and 10+ language support. LinkDM is capable, but it limits DMs by plan, sells credit add-ons, and doesn't publicly offer AI replies or growth attribution.",
  },
  {
    question: "What is the best LinkDM alternative?",
    answer:
      "Zapify is a strong LinkDM alternative for creators who want unlimited DMs, AI-powered Instagram automation, detailed growth insights, ask-to-follow flows, email capture, templates, and creator-friendly pricing. Unlike LinkDM, Zapify doesn't cap your DM volume or sell credit packages on paid plans.",
  },
  {
    question: "Why switch from LinkDM to Zapify?",
    answer:
      "Switch to Zapify if you want to move beyond capped AutoDM plans and credit top-ups. Zapify gives you unlimited DMs, AI replies, Zapify growth attribution, ask-to-follow flows, smart offers, templates, and language switching in a pricing model that doesn't charge you more when your content performs well.",
  },
  {
    question: "Does Zapify have unlimited DMs?",
    answer:
      "Yes. Zapify's paid plans include unlimited DMs and unlimited automations. There are no monthly caps and no DM credit packages. LinkDM's public plans list monthly DM limits and sell additional credit packages when you exceed them.",
  },
  {
    question: "Does LinkDM have unlimited DMs?",
    answer:
      "No. LinkDM's public plans list monthly DM caps: 1,000 on the free plan, 25,000 on Pro, and 300,000 on Platinum+. DM credit add-ons are available for purchase when you exceed plan limits.",
  },
  {
    question: "Does Zapify have AI replies?",
    answer:
      "Yes. Zapify is an AI-powered Instagram comment management and automation platform. The AI understands context, matches your brand voice, and generates replies that feel personal, not scripted.",
  },
  {
    question: "Does LinkDM have AI replies?",
    answer:
      "AI-powered comment replies and brand voice controls were not publicly available on the LinkDM pages reviewed for this comparison as of May 8, 2026. LinkDM automates DM delivery but doesn't publicly offer smarter, context-aware AI replies.",
  },
  {
    question: "How does Zapify pricing compare with LinkDM?",
    answer:
      "Zapify has a free 30-day trial, then flat plans at $9.99/month and $19.99/month, plus a one-time lifetime option. LinkDM's first paid tier is $19/month for 25,000 DMs/month, with credit packages available beyond that. Zapify Starter is cheaper than LinkDM Pro and removes the DM cap entirely.",
  },
  {
    question: "Who should still consider LinkDM?",
    answer:
      "LinkDM may make sense if you specifically need Facebook AutoDM, Rewind for backsending DMs to older comments, Story Mention automation, DM overflow queue controls, slow-down mode, click analytics, or paid DM credit packages for burst sending.",
  },
]

// ─── Feature Table Data ──────────────────────────────────────
type FeatureRow = {
  name: string
  zapify: string | boolean
  linkdm: string | boolean
  zapifyNote?: string
  linkdmNote?: string
}

type FeatureCategory = {
  category: string
  rows: FeatureRow[]
}

const featureCategories: FeatureCategory[] = [
  {
    category: "Instagram automation",
    rows: [
      { name: "Instagram comment-to-DM automation", zapify: true, linkdm: true, zapifyNote: "Personalized DMs with links, offers, and content" },
      { name: "Bulk DM automation", zapify: true, linkdm: true, zapifyNote: "Unlimited DMs on paid plans", linkdmNote: "Monthly cap; credits beyond cap" },
      { name: "Unlimited DMs on paid plans", zapify: true, linkdm: false, zapifyNote: "No monthly cap", linkdmNote: "Monthly DM limits" },
      { name: "Post and Reel automation", zapify: true, linkdm: true },
      { name: "Multiple selected posts", zapify: true, linkdm: false, linkdmNote: "Not publicly confirmed" },
      { name: "Any-post automation", zapify: true, linkdm: false, zapifyNote: "Evergreen automation", linkdmNote: "Not publicly confirmed" },
      { name: "Story DM automation", zapify: true, linkdm: true },
      { name: "Story Mention automation", zapify: false, linkdm: true, zapifyNote: "Not publicly confirmed" },
      { name: "Facebook AutoDM", zapify: false, linkdm: true, zapifyNote: "Not a Zapify focus" },
    ],
  },
  {
    category: "AI, growth, and conversion",
    rows: [
      { name: "AI-powered comment replies", zapify: true, linkdm: false, zapifyNote: "Built-in contextual AI", linkdmNote: "Not publicly available" },
      { name: "Brand voice controls", zapify: true, linkdm: false, zapifyNote: "Tone, length, style", linkdmNote: "Not publicly available" },
      { name: "Ask-to-follow before sharing content", zapify: true, linkdm: false, zapifyNote: "Native growth flow", linkdmNote: "No clear equivalent" },
      { name: "Email capture in DMs", zapify: true, linkdm: true, linkdmNote: "Lead Gen" },
      { name: "Detailed Instagram Reel insights", zapify: true, linkdm: false, linkdmNote: "No public equivalent" },
      { name: "Followers gained attribution", zapify: true, linkdm: false, linkdmNote: "No public equivalent" },
      { name: "Leads gained attribution", zapify: true, linkdm: false, linkdmNote: "No public equivalent" },
    ],
  },
  {
    category: "Setup, accounts, and business model",
    rows: [
      { name: "Templates for quick setup", zapify: true, linkdm: true, zapifyNote: "Launch in minutes" },
      { name: "Smart offers / link cards in DMs", zapify: true, linkdm: true, linkdmNote: "URL button templates" },
      { name: "Multiple Instagram accounts", zapify: true, linkdm: true, zapifyNote: "Up to 6 profiles", linkdmNote: "Up to 10 on Platinum+" },
      { name: "Team access", zapify: true, linkdm: false, zapifyNote: "From Starter", linkdmNote: "Not publicly confirmed" },
      { name: "10+ language switching", zapify: true, linkdm: false, linkdmNote: "Not publicly confirmed" },
      { name: "Lifetime access option", zapify: true, linkdm: false, zapifyNote: "$200 one-time" },
      { name: "Paid DM credit packages", zapify: false, linkdm: true, zapifyNote: "Not needed on paid plans", linkdmNote: "$5-$350 add-ons" },
      { name: "Rewind / backsend to older comments", zapify: false, linkdm: true, zapifyNote: "Not publicly confirmed" },
      { name: "DM overflow queue", zapify: false, linkdm: true, zapifyNote: "Not publicly confirmed", linkdmNote: "Platinum+" },
      { name: "Slow-down / rate-limit mode", zapify: false, linkdm: true, zapifyNote: "Not publicly confirmed" },
      { name: "Click analytics on DMs", zapify: false, linkdm: true, zapifyNote: "Not publicly confirmed" },
      { name: "Meta / partner status", zapify: true, linkdm: true, zapifyNote: "Approved by Meta - verify wording", linkdmNote: "Meta Business Partner since 2021" },
    ],
  },
]

// ─── Pricing Data ────────────────────────────────────────────
const pricingTiers = [
  { label: "Free / trial", zapify: "$0 for 30 days", linkdm: "$0", note: "Zapify includes 1,000 DMs + 1,000 AI replies" },
  { label: "Entry paid plan", zapify: "$9.99/mo", linkdm: "$19/mo", note: "Zapify Starter includes unlimited DMs" },
  { label: "Professional", zapify: "$19.99/mo", linkdm: "$99/mo", note: "Zapify unlocks unlimited AI replies; LinkDM Platinum+ raises DM cap" },
  { label: "Lifetime access", zapify: "$200 once", linkdm: "Not available", note: "Zapify offers a one-time option with no recurring fees" },
]

const problemCards = [
  {
    title: "LinkDM caps how many DMs you can send",
    body:
      "LinkDM's Pro plan allows 25,000 DMs per month and Platinum+ allows 300,000. Those limits sound large until a Reel gets picked up by the algorithm. When you hit your cap, you either buy credits or leave comments unanswered.",
  },
  {
    title: "LinkDM sells DM credits",
    body:
      "Additional credit packages make your monthly bill a function of how well your content performs. Zapify's pricing is flat, so a successful campaign does not turn into a surprise billing decision.",
  },
  {
    title: "LinkDM has no public AI replies",
    body:
      "LinkDM sends automated DMs, but it does not publicly offer AI that writes smarter responses. Zapify's AI understands context, adapts to your brand voice, and helps conversations feel personal.",
  },
]

const chooseZapifyIf = [
  "Want unlimited DMs on paid plans - no monthly caps, no credit purchases",
  "Want AI-powered replies that understand context and sound like your brand",
  "Want brand voice controls for tone, reply length, and style",
  "Want bulk DM automation for comments, Reels, Stories, selected posts, or any eligible post",
  "Want ask-to-follow flows that grow your follower count before delivering gated content",
  "Want to collect emails inside Instagram DM conversations",
  "Want to know which posts and automations drive followers and leads",
  "Want templates, smart offers, 10+ language support, and team access",
  "Want to pay once with lifetime access instead of stacking monthly bills",
]

const chooseLinkDMIf = [
  "Need Facebook AutoDM in the same tool as Instagram",
  "Need Story Mention automation",
  "Need Rewind to backsend DMs to older eligible comments",
  "Need DM overflow queue controls",
  "Need slow-down mode or rate-limiting for high-volume accounts",
  "Need click analytics on DMs sent",
  "Prefer paid DM credit packages for burst campaigns",
]

const differentiators = [
  {
    num: "01",
    title: "Unlimited DMs - no math required",
    body:
      "LinkDM's model makes you think about volume constantly. Zapify removes that. Paid plans include unlimited DMs and unlimited automations, so when a Reel performs, Zapify handles every comment.",
  },
  {
    num: "02",
    title: "AI that replies like you, not a script",
    body:
      "Zapify's built-in AI understands what someone is asking and responds in context, matching your brand voice and making the DM feel personal.",
  },
  {
    num: "03",
    title: "Growth attribution you can act on",
    body:
      "Zapify tracks Instagram metrics and Zapify-specific outcomes like followers gained and leads captured, so automation becomes something you can learn from.",
  },
  {
    num: "04",
    title: "Evergreen automation across any post",
    body:
      "Zapify lets you run automation on one post, selected posts, or every eligible post at once, so campaigns keep running without rebuilding them every time you publish.",
  },
  {
    num: "05",
    title: "Ask-to-follow converts comments into followers",
    body:
      "Before sending a resource or offer, Zapify can ask someone to follow first. That turns high-intent commenters into followers, not just one-off clicks.",
  },
  {
    num: "06",
    title: "Creator-friendly pricing from day one",
    body:
      "Zapify's free trial, $9.99 Starter plan, and $200 lifetime option make it easier for creators to scale without a bill that grows every time content works.",
  },
]

// ─── Route ───────────────────────────────────────────────────
export const Route = createFileRoute("/comparison/linkdm-vs-zapify-pro")({
  head: () => ({
    meta: [
      ...createLLMOMeta({
        title: "Zapify vs LinkDM: Best LinkDM Alternative for Instagram DM Automation",
        description:
          "Compare Zapify vs LinkDM for Instagram DM automation, AI replies, bulk DMs, Story DMs, insights, pricing, and creator growth. See why creators switch to Zapify.",
        url: "https://zapify.pro/comparison/linkdm-vs-zapify-pro",
        publishedTime: "2026-05-08",
        modifiedTime: "2026-05-08",
        ogType: "article",
      }),
      { property: "og:image", content: "https://cms.zapify.pro/uploads/OG_image1_f82f5871e2.png" },
      { property: "og:locale", content: "en_US" },
      { name: "keywords", content: "Zapify vs LinkDM, LinkDM alternative, LinkDM vs Zapify, Instagram DM automation tool, Instagram comment to DM automation, bulk Instagram DM automation, AI Instagram replies, Story DM automation" },
    ],
    links: [
      { rel: "canonical", href: "https://zapify.pro/comparison/linkdm-vs-zapify-pro" },
    ],
    scripts: [
      createBreadcrumbSchema({
        itemListElement: [
          { position: 1, name: "Home", item: "https://zapify.pro" },
          { position: 2, name: "Comparisons", item: "https://zapify.pro/comparison" },
          { position: 3, name: "LinkDM vs Zapify", item: "https://zapify.pro/comparison/linkdm-vs-zapify-pro" },
        ],
      }),
      createFAQSchema(faqs),
      createArticleSchema({
        headline: "Zapify vs LinkDM: Best LinkDM Alternative for Instagram DM Automation",
        description: "A practical comparison of Zapify and LinkDM for Instagram DM automation, bulk DMs, AI replies, Story DMs, pricing, and creator growth.",
        image: "https://cms.zapify.pro/uploads/OG_image1_f82f5871e2.png",
        author: { name: "Zapify", url: "https://zapify.pro" },
        datePublished: "2026-05-08",
        dateModified: "2026-05-08",
        publisher: {
          name: "Zapify Pro",
          url: "https://zapify.pro",
          logo: "https://zapify.pro/logo-small.png",
          description: "AI-Powered Instagram Comment Management & Automation",
        },
      }),
    ],
  }),
  component: LinkDMComparisonPage,
})

// ─── Page Component ──────────────────────────────────────────
export function LinkDMComparisonPage() {
  const { track } = useAnalytics()
  const { openAuth } = useAuth()
  const [openFaq, setOpenFaq] = useState(0)

  const handleCTA = (ctaId: string, buttonText: string) => {
    track("cta_clicked", { cta_id: ctaId, button_text: buttonText })
    openAuth()
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-white dark:bg-black transition-colors duration-300">
      {/* Radial fade background */}
      <div className="absolute inset-0 bg-white/50 dark:bg-black/20 [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none" />
      {/* Dotted background */}
      <div className="absolute inset-0 bg-[radial-gradient(circle,#80808015_1px,transparent_1px)] bg-[size:20px_20px] pointer-events-none" />

      <div className="relative z-10 pt-28 sm:pt-36 pb-12">
        {/* Section 1: Hero */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 text-center mb-16 sm:mb-20">
          <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-white dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 shadow-sm mb-6">
            <span className="text-neutral-600 dark:text-neutral-300 font-semibold text-xs">Updated May 2026</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-neutral-900 dark:text-white mb-4 sm:mb-6 tracking-tight max-w-5xl mx-auto">
            <span className="gradient-text">Zapify vs</span> LinkDM
          </h1>
          <p className="text-xl sm:text-2xl md:text-3xl font-bold text-neutral-900 dark:text-white max-w-4xl mx-auto mb-5 tracking-tight">
            LinkDM caps your DMs. Zapify doesn't.
          </p>
          <p className="text-base sm:text-lg md:text-xl text-neutral-600 dark:text-neutral-400 font-medium max-w-3xl mx-auto mb-8 sm:mb-10">
            Zapify is built around unlimited DMs, AI-powered replies, and real growth attribution. You focus on creating; Zapify handles the rest.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Button
              onClick={() => handleCTA("CTA_LinkDM_Hero", "Start Free 30 Days")}
              className="bg-primary-gradient text-primary-foreground hover:brightness-[0.97] active:brightness-[0.92] font-bold px-8 py-6 text-base rounded-full shadow-lg"
            >
              Start Free 30 Days
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="px-8 py-6 text-base rounded-full font-semibold border-neutral-200 dark:border-neutral-700"
              onClick={() => {
                document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })
              }}
            >
              See Pricing
            </Button>
          </div>
        </section>

        {/* Ask AI to Explain */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <AskAIExplain
            context="comparison"
            meta={{ competitorName: "LinkDM", pageUrl: "https://zapify.pro/comparison/linkdm-vs-zapify-pro" }}
            isCard
            isWidget
          />
        </section>

        {/* Section 2: Quick Answer Box */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20" aria-label="Featured summary">
          <article className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-6 sm:p-8 shadow-sm">
            <p className="text-xs font-bold uppercase tracking-widest text-brand-button mb-3">
              Bottom line - which is better, Zapify or LinkDM?
            </p>
            <p className="text-sm sm:text-base text-neutral-600 dark:text-neutral-400 leading-relaxed mb-4">
              <strong className="text-neutral-900 dark:text-white">Zapify is the better choice for Instagram creators who want to grow.</strong>{" "}
              LinkDM is built around monthly DM limits and paid credit packages - a model that works at low volume but becomes a budget problem the moment a post goes viral.
            </p>
            <p className="text-sm sm:text-base text-neutral-600 dark:text-neutral-400 leading-relaxed">
              Choose Zapify if you want <strong className="gradient-text">AI replies</strong>, bulk DM automation, and growth insights without counting DM credits. LinkDM is worth considering only if you specifically need Facebook AutoDM, Rewind, or advanced queue controls.
            </p>
          </article>
        </section>

        {/* Section 3: Problem With LinkDM */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white tracking-tight">
              The problem with <span className="gradient-text">LinkDM</span> for growing creators
            </h2>
            <p className="text-base sm:text-lg text-neutral-600 dark:text-neutral-400 max-w-3xl mx-auto mt-3">
              LinkDM is a capable AutoDM tool, but its model is built for controlled, predictable volume - not fast creator growth.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {problemCards.map((item) => (
              <article key={item.title} className="rounded-xl border border-red-200 dark:border-red-800/40 bg-red-50/50 dark:bg-red-900/10 p-5 sm:p-6">
                <div className="w-9 h-9 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
                  <X className="w-4 h-4 text-red-500 dark:text-red-400" strokeWidth={3} />
                </div>
                <h3 className="text-base font-bold text-neutral-900 dark:text-white mb-2">{item.title}</h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">{item.body}</p>
              </article>
            ))}
          </div>
        </section>

        {/* Section 4: Choose Tool */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-2xl border-2 border-green-200 dark:border-green-800/50 bg-white dark:bg-neutral-900/50 p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <h2 className="text-lg font-bold text-green-600 dark:text-green-400">Choose Zapify if you…</h2>
              </div>

              <ul className="space-y-2.5">
                {chooseZapifyIf.map((item) => (
                  <li key={item} className="flex items-start gap-2.5">
                    <span className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                      <Check className="w-3 h-3" strokeWidth={3} />
                    </span>
                    <span className="text-sm text-neutral-700 dark:text-neutral-300 leading-snug">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border-2 border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900/50 p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-neutral-500 dark:text-neutral-400" />
                </div>
                <h2 className="text-lg font-bold text-neutral-600 dark:text-neutral-400">Choose LinkDM only if you…</h2>
              </div>

              <ul className="space-y-2.5">
                {chooseLinkDMIf.map((item) => (
                  <li key={item} className="flex items-start gap-2.5">
                    <span className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400">
                      <Check className="w-3 h-3" strokeWidth={3} />
                    </span>
                    <span className="text-sm text-neutral-600 dark:text-neutral-400 leading-snug">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Section 3: Feature Comparison Table */}
        <section id="feature-table" className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white tracking-tight">
              How does Zapify compare to LinkDM feature{" "}
              <span className="gradient-text">by feature?</span>
            </h2>
            <Paragraph className="max-w-3xl mx-auto">
              A transparent, side-by-side breakdown of every Instagram automation capability that matters in 2026.
            </Paragraph>
          </div>

          <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-sm bg-white dark:bg-neutral-900/50">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-neutral-50/80 dark:bg-neutral-900/80 border-b border-neutral-200 dark:border-neutral-800">
                    <th className="text-left px-4 sm:px-6 py-4 font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider text-xs w-[40%]">
                      Feature
                    </th>
                    <th className="text-left px-4 sm:px-6 py-4 uppercase tracking-wider text-xs w-[30%] text-green-700 dark:text-green-300">
                      Zapify
                    </th>
                    <th className="text-left px-4 sm:px-6 py-4 font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider text-xs w-[30%]">
                      LinkDM
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
                  {featureCategories.flatMap((cat) => [
                    <tr key={`cat-${cat.category}`} className="bg-neutral-50/70 dark:bg-neutral-900/70">
                      <td colSpan={3} className="px-4 sm:px-6 py-2.5 text-xs font-bold uppercase tracking-widest text-brand-button">
                        {cat.category}
                      </td>
                    </tr>,
                    ...cat.rows.map((row) => (
                      <tr key={`${cat.category}-${row.name}`} className="hover:bg-neutral-50/50 dark:hover:bg-neutral-800/20 transition-colors align-top">
                        <td className="px-4 sm:px-6 py-3.5 font-medium text-neutral-700 dark:text-neutral-300">{row.name}</td>
                        <td className="px-4 sm:px-6 py-3.5">
                          <div className="flex flex-col gap-1.5">
                            <span className="text-sm font-semibold text-neutral-800 dark:text-neutral-100">{typeof row.zapify === "boolean" ? (row.zapify ? "Yes" : "No") : row.zapify}</span>
                            {row.zapifyNote ? <span className="text-xs text-green-700 dark:text-green-300">{row.zapifyNote}</span> : null}
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-3.5">
                          <div className="flex flex-col gap-1.5">
                            <span className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">{typeof row.linkdm === "boolean" ? (row.linkdm ? "Yes" : "No") : row.linkdm}</span>
                            {row.linkdmNote ? <span className="text-xs text-red-600 dark:text-red-300">{row.linkdmNote}</span> : null}
                          </div>
                        </td>
                      </tr>
                    )),
                  ])}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Section 6: Pricing Breakdown */}
        <section id="pricing" className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white tracking-tight">
              Zapify vs LinkDM <span className="gradient-text">pricing</span>
            </h2>
            <Paragraph className="max-w-2xl mx-auto">
              Flat, predictable creator pricing versus monthly caps plus credit add-ons.
            </Paragraph>
          </div>

          <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 overflow-hidden">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-neutral-50/80 dark:bg-neutral-900/80 border-b border-neutral-200 dark:border-neutral-800">
                  <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Plan stage</th>
                  <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wide text-green-700 dark:text-green-300">Zapify</th>
                  <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600 dark:text-neutral-300">LinkDM</th>
                  <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Why it matters</th>
                </tr>
              </thead>
              <tbody>
                {pricingTiers.map((tier) => (
                  <tr key={tier.label} className="align-top border-b last:border-b-0 border-neutral-100 dark:border-neutral-800/80">
                    <td className="px-4 sm:px-5 py-4 text-sm font-semibold text-neutral-800 dark:text-neutral-100">{tier.label}</td>
                    <td className="px-4 sm:px-5 py-4 text-lg font-black tracking-tight text-green-700 dark:text-green-300">{tier.zapify}</td>
                    <td className="px-4 sm:px-5 py-4 text-lg font-black tracking-tight text-neutral-700 dark:text-neutral-100">{tier.linkdm}</td>
                    <td className="px-4 sm:px-5 py-4">{tier.note ? <span className="text-xs sm:text-sm text-green-700 dark:text-green-300 font-semibold">{tier.note}</span> : <span className="text-xs text-neutral-500 dark:text-neutral-400">Equal base price</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8 rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-6 sm:p-8 text-center">
            <p className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 mb-2">The comparison that matters</p>
            <p className="text-2xl sm:text-4xl font-black tracking-tight text-neutral-900 dark:text-white">
              Zapify Starter gives you <span className="gradient-text">unlimited DMs</span> for $9.99/month.
            </p>
            <p className="text-sm sm:text-base text-neutral-600 dark:text-neutral-400 mt-3 max-w-3xl mx-auto">
              LinkDM Pro is $19/month for 25,000 DMs, with credit packages available after you exceed that. For creators building an audience, Zapify removes the ceiling.
            </p>
          </div>

          <div className="mt-8">
            <LifetimeAccessPricing
              spotsLeft={87}
              totalSpots={100}
              price={200}
              originalMonthlyPrice={39}
              onGetAccess={() => handleCTA("CTA_LinkDM_Lifetime_Pricing", "Claim Lifetime Access")}
            />
          </div>
        </section>

        {/* Section 7: Why Zapify Wins */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white tracking-tight">
              What makes Zapify the better <span className="gradient-text">growth tool</span>
            </h2>
            <Paragraph className="max-w-2xl mx-auto">
              Six reasons Instagram-first creators choose Zapify over LinkDM.
            </Paragraph>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {differentiators.map((item) => (
              <article key={item.num} className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-5 sm:p-6">
                <div className="flex items-start gap-4">
                  <span className="text-2xl font-black text-brand-button/30 dark:text-brand-button/20 leading-none shrink-0">{item.num}</span>
                  <div>
                    <h3 className="font-bold text-neutral-900 dark:text-white text-sm mb-1.5">{item.title}</h3>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">{item.body}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* Section 8: Honest Verdict */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <article className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-6 sm:p-8 text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white tracking-tight mb-4">
              Honest verdict: <span className="gradient-text">Zapify wins</span>
            </h2>
            <p className="text-sm sm:text-base text-neutral-600 dark:text-neutral-400 leading-relaxed max-w-4xl mx-auto">
              LinkDM works for basic AutoDM, but it was built around controlled volume and credit packs. Zapify removes the friction with unlimited DMs, built-in AI replies, real growth attribution, faster setup, and pricing that does not charge you more for performing well.
            </p>
          </article>
        </section>

        {/* Section 6: FAQ */}
        <section className="w-full py-12 sm:py-20 px-4 sm:px-6 border-b border-neutral-100 dark:border-white/5">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8 text-center sm:mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold text-[#181C31] dark:text-white tracking-tight">
                Frequently <span className="gradient-text">Asked Questions</span>
              </h2>
              <p className="text-sm sm:text-base md:text-xl text-[#64657E] dark:text-gray-400 font-medium max-w-2xl mx-auto mt-3 sm:mt-5">
                Everything you need to know about LinkDM vs Zapify
              </p>
            </div>

            <div className="mx-auto max-w-5xl space-y-3">
              {faqs.map((faq, index) => {
                const isOpen = openFaq === index
                return (
                  <div key={faq.question} className="rounded-2xl border border-border/50 bg-card/90 px-4 py-3.5 sm:px-6 sm:py-4 dark:bg-card/80">
                    <button type="button" onClick={() => setOpenFaq((prev) => (prev === index ? -1 : index))} className="flex w-full items-center justify-between gap-3 text-left">
                      <div className="flex items-center gap-3">
                        <span className="text-base leading-none">{["⚡", "💸", "🤖", "🔄", "📩", "🛡️"][index] ?? "⚡"}</span>
                        <p className="text-base font-semibold leading-snug text-foreground sm:text-[1.15rem]">{faq.question}</p>
                      </div>
                      <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground">{isOpen ? "−" : "+"}</span>
                    </button>
                    <div className={`grid transition-all duration-300 ${isOpen ? "mt-2.5 grid-rows-[1fr] opacity-100 sm:mt-3" : "grid-rows-[0fr] opacity-0"}`}>
                      <p className="overflow-hidden pr-3 text-[13px] leading-relaxed text-muted-foreground sm:pr-8 sm:text-[0.95rem]">{faq.answer}</p>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

        </section>

        {/* Section 10: Other Comparisons */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 border-b border-neutral-100 dark:border-white/5">
          <div className="text-center mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-white">
              Compare Zapify vs <span className="gradient-text">other tools</span>
            </h2>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { label: "Zapify vs ManyChat", href: "/comparison/manychat-vs-zapify-pro" },
              { label: "Zapify vs Zorcha", href: "/comparison/zorcha-vs-zapify-pro" },
              { label: "Zapify vs InstantDM", href: "/comparison/instantdm-vs-zapify-pro" },
              { label: "Zapify vs ReplyRush", href: "/comparison/replyrush-vs-zapify-pro" },
              { label: "Zapify vs SuperProfile", href: "/comparison/superprofile-vs-zapify-pro" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="px-5 py-2.5 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 text-sm font-semibold text-neutral-700 dark:text-neutral-300 hover:border-neutral-300 dark:hover:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-all duration-200"
              >
                {link.label}
              </a>
            ))}
          </div>
        </section>

        {/* Section 11: Bottom CTA */}
        <section className="relative overflow-hidden py-16 sm:py-24 px-4 sm:px-6">
          {/* Gradient blob */}
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-brand-accent/10 rounded-full blur-3xl" />
          </div>

          <div className="relative max-w-2xl mx-auto text-center">
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-neutral-900 dark:text-white mb-4 sm:mb-6 tracking-tight">
              Stop counting credits. <span className="gradient-text">Start growing.</span>
            </h2>
            <p className="text-base sm:text-lg text-neutral-600 dark:text-neutral-400 mb-8 sm:mb-10 max-w-xl mx-auto">
              Start free, launch your first automation in minutes, and use AI-powered Instagram automation without DM credit math.
            </p>

            <Button
              onClick={() => handleCTA("CTA_Comparison_Bottom", "Start Free with Zapify")}
              className="bg-primary-gradient text-primary-foreground hover:brightness-[0.97] active:brightness-[0.92] font-bold px-10 py-6 text-lg rounded-full shadow-lg"
            >
              Start Free with Zapify
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>

            <p className="mt-4 text-sm text-neutral-500 dark:text-neutral-400">
              Free forever tier &middot; No credit card &middot; 1-minute setup
            </p>
          </div>
        </section>

        {/* Disclaimer */}
        <ContentSection className="max-w-7xl mx-auto px-4 sm:px-6 pb-8">
          <p className="text-xs text-neutral-400 dark:text-neutral-500 text-center">
            LinkDM is a trademark of its respective owner. Zapify is not affiliated with, endorsed by, or sponsored by LinkDM. Feature and pricing information is based on publicly available information and Zapify research as of May 8, 2026. Always check each provider's website for current pricing and plan details before purchasing.
          </p>
        </ContentSection>
      </div>
    </div>
  )
}
